import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdfIcon = "/img/pdf1.png";

export default function AeroTable12({ data }) {
  const pdfRef = useRef();

  const filterData = data.filter(
    (item) =>
      item.age_group_label === "14-18" || item.age_group_label === "22-24",
  );

  // =============================
  // Summary Calculations
  // =============================
  const totalMale = filterData.reduce(
    (sum, item) => sum + parseInt(item.male_2024 || 0, 10),
    0,
  );

  const highestGroup = filterData.reduce(
    (max, item) =>
      parseInt(item.male_2024, 10) > max.value
        ? { label: item.age_group_label, value: parseInt(item.male_2024, 10) }
        : max,
    { label: "", value: 0 },
  );

  // =============================
  // Chart Data
  // =============================
  const chartData = {
    labels: filterData.map((item) => item.age_group_label),
    datasets: [
      {
        label: "Male Employment (2024)",
        data: filterData.map((item) => parseInt(item.male_2024, 10)),
        backgroundColor: ["#0ea5e9", "#6366f1"],
        borderRadius: 8,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Male Employment by Selected Age Groups (2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: { display: true, text: "Number of Workers" },
      },
      x: {
        title: { display: true, text: "Age Group" },
        grid: { display: false },
      },
    },
  };

  // =============================
  // PDF Download
  // =============================
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Header
      pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
      pdf.setFontSize(26);
      pdf.text("UTahData.org", 95, 22);
      pdf.line(10, 35, 200, 35);

      pdf.addImage(imgData, "PNG", 5, 38, imgWidth, imgHeight);

      // Footer
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("male_employment_2024.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Male Employment by Selected Age Groups (2024)
        </h2>

        {/* ================= Summary Cards ================= */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Male Employment (Selected Groups)
            </p>
            <p className="text-xl font-bold text-blue-800">
              {totalMale.toLocaleString("en-US")}
            </p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Highest Employment Group
            </p>
            <p className="text-xl font-bold text-green-800">
              {highestGroup.label}
            </p>
          </div>
        </div>

        {/* ================= Table + Chart ================= */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* TABLE */}
          <div className="overflow-x-auto shadow-md rounded-lg">
            <table className="w-full border-collapse text-center">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Age Group</th>
                  <th className="p-2">Male Employment (2024)</th>
                </tr>
              </thead>
              <tbody>
                {filterData.map((row, index) => (
                  <tr
                    key={index}
                    className={`hover:bg-gray-100 transition ${
                      row.age_group_label === highestGroup.label
                        ? "bg-green-100 font-bold"
                        : ""
                    }`}
                  >
                    <td className="p-2">{row.age_group_label}</td>
                    <td className="p-2">
                      {parseInt(row.male_2024, 10).toLocaleString("en-US")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* CHART */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="min-w-[400px] w-full h-96">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* ================= Download Button ================= */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
